// ViewDocPdfFrame — the document itself, rendered INLINE.
//
//   Miles: "Shouldn't have to download a PDF etc just view everything."
//
// So there is deliberately no download button here. The bytes are fetched with
// the session token (the /file route is authenticated like every other API
// route), re-wrapped as a blob: URL, and handed to an <iframe> — which means a
// half-signed document is simply readable, because GET
// /submissions/{id}/documents?merge=true returns the partial combined PDF even
// while PENDING (WEL-29). That is what makes "view it in any state" true.
//
// When there is no PDF yet the component says so in the server's own words
// rather than rendering an empty grey rectangle. One of those words is
// `redacted`, and it is not an error: a W-9's panel and timeline are fully
// visible, but its bytes never are, because the PDF carries the owner's
// SSN or EIN. That refusal is the feature working, so it reads as a statement
// rather than a failure.
//
// TOUCH DEVICES (2026-09-24): an iframed PDF on mobile Safari shows only its
// FIRST page — the rest of a lease is unreachable. On a coarse pointer the same
// bytes render as page images instead (components/PdfPages, the renderer the
// public sign page uses), every page, scrolling with the page. Desktop keeps
// the browser's own viewer (search, zoom, print). If the page renderer cannot
// read the file, it falls back to the iframe.

import { useEffect, useRef, useState } from 'react';
import { ApiError } from '@/lib/api';
import { OcEmpty, OcError } from '@/components/oc/bits';
import { PdfPages } from '@/components/PdfPages';
import { fetchDocFile, type DocFile } from '@/lib/viewDocsClient';

/** A touch-first device, where an iframed PDF cannot be relied on to show
 *  more than its first page. Read once per mount; false during SSR. */
function isCoarsePointer(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}

function loadError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    if (err.status === 404) {
      return (
        body?.message ??
        'There is no document to show for this version yet.'
      );
    }
    return body?.message ?? body?.error ?? `Couldn't load the document (HTTP ${err.status}).`;
  }
  return err instanceof Error ? err.message : "Couldn't load the document.";
}

export function ViewDocPdfFrame({
  kind,
  id,
  versionId,
  title,
  availability,
}: {
  kind: string;
  id: string;
  versionId?: string | null;
  /** Accessible name for the frame — "Lease for Jose Cuevas Cortez". */
  title: string;
  /** From DocDetail.file. `available: false` short-circuits the fetch, and
   *  `file.url` is used verbatim when present — it is already version-scoped. */
  availability?: DocFile;
}): React.JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [coarse] = useState(isCoarsePointer);
  // Touch devices: the bytes for the page renderer, and whether it gave up
  // (→ the iframe fallback).
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
  const [pagesFailed, setPagesFailed] = useState(false);
  // Object URLs handed to a popped-out tab: never revoked while this component
  // lives, so the tab the operator opened keeps working.
  const poppedRef = useRef<string[]>([]);

  const unavailable = availability?.available === false;
  const redacted = availability?.reason === 'redacted';
  const fileUrl = availability?.url;

  useEffect(() => {
    if (unavailable) {
      setUrl(null);
      setError(null);
      return;
    }
    let live = true;
    let objectUrl: string | null = null;
    setLoading(true);
    setError(null);
    setBytes(null);
    setPagesFailed(false);
    void (async () => {
      try {
        const blob = await fetchDocFile(kind, id, versionId, fileUrl);
        if (!live) return;
        if (coarse) {
          const buf = await blob.arrayBuffer();
          if (!live) return;
          setBytes(buf);
        }
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch (err) {
        if (live) setError(loadError(err));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [kind, id, versionId, unavailable, fileUrl, attempt, coarse]);

  useEffect(() => {
    const popped = poppedRef.current;
    return () => {
      for (const u of popped) URL.revokeObjectURL(u);
    };
  }, []);

  const popOut = () => {
    if (!url) return;
    // A second, independent handle: revoking this component's URL on a version
    // switch must not blank the tab the operator just opened.
    void (async () => {
      try {
        const blob = await fetchDocFile(kind, id, versionId, fileUrl);
        const fresh = URL.createObjectURL(blob);
        poppedRef.current.push(fresh);
        window.open(fresh, '_blank', 'noopener,noreferrer');
      } catch {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    })();
  };

  if (unavailable) {
    // Redaction is a deliberate refusal, not a gap — say so in its own voice,
    // and never offer a retry that could only fail on purpose.
    if (redacted) {
      return (
        <div className="alert" data-tone="info">
          <div className="alert-body">
            {availability?.note ??
              'This document is not shown here because it carries a taxpayer identification number. Its details and its full history are above.'}
          </div>
        </div>
      );
    }
    return (
      <OcEmpty>
        {availability?.note ?? 'No document has been produced for this version yet.'}
      </OcEmpty>
    );
  }

  if (error) {
    return (
      <OcError>
        <div>{error}</div>
        <div style={{ marginTop: 'var(--space-3)' }}>
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            onClick={() => setAttempt((n) => n + 1)}
          >
            Try again
          </button>
        </div>
      </OcError>
    );
  }

  const showPages = coarse && !pagesFailed;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
          marginBottom: 'var(--space-2)',
        }}
      >
        <span className="oc-kicker">Document — as it stands right now</span>
        <span style={{ display: 'inline-flex', gap: 'var(--space-2)' }}>
          {showPages ? null : (
            <button
              type="button"
              className="btn"
              data-variant="ghost"
              data-size="sm"
              disabled={!url}
              aria-pressed={expanded}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? 'Shrink' : 'Fill the screen'}
            </button>
          )}
          <button
            type="button"
            className="btn"
            data-variant="ghost"
            data-size="sm"
            disabled={!url}
            onClick={popOut}
            title="Opens this document in its own tab — still viewing, nothing is saved to your computer."
          >
            Open in a tab
          </button>
        </span>
      </div>
      <div className="vd-frame-wrap" data-expanded={expanded ? 'true' : undefined}>
        {showPages && bytes ? (
          <div style={{ padding: 'var(--space-3)' }}>
            <PdfPages data={bytes} title={title} onError={() => setPagesFailed(true)} />
          </div>
        ) : url ? (
          <iframe className="vd-frame" src={url} title={title} />
        ) : (
          <div className="oc-empty">{loading ? 'Loading the document…' : 'No document.'}</div>
        )}
      </div>
    </div>
  );
}
